import { useState, useEffect } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import pageStyles from '../styles/page-common.module.css';
import formStyles from '../styles/form-common.module.css';
import { createUser, checkRegDetails } from '../backend';


const Register = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate(); // Get the navigate function
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [agreeMarketingComms, setAgreeMarketingComms] = useState(false);
  const [agreeReminders, setAgreeReminders] = useState(false);
  const isLoggedIn = useSelector((state) => state.user).isLoggedIn;
  const [userCreated, setUserCreated] = useState(false);

  // shouldn't be here if logged in; navigate to home
  useEffect(() => {
    if (isLoggedIn) {
      navigate('/');
    }
  }, [navigate, isLoggedIn]);

  // when user created, navigate to success page
  useEffect(() => {
    if (userCreated) {
      navigate('/registration-success');
    }    
  }, [userCreated, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    if (name === 'agreeTerms') setAgreeTerms(checked);
    if (name === 'agreePrivacy') setAgreePrivacy(checked);
    if (name === 'agreeMarketingComms') setAgreeMarketingComms(checked);
    if (name === 'agreeReminders') setAgreeReminders(checked);
  };

  // Username validation: alphanumeric and < 30 characters
  const isUsernameValid = (username) => {
    const usernameRegex = /^[a-zA-Z0-9]+$/; // only letters and numbers
    return username.length <= 30 && usernameRegex.test(username);
  };

  // Password validation: min 8 chars, mix of letters, numbers, special chars
  const isPasswordStrong = (password) => {
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/;
    return passwordRegex.test(password);
  };

  // handle registration form submission
  const handleRegistration = async (e) => {

    e.preventDefault();
    setErrorMessage('');

    // check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    // validate username
    if (!isUsernameValid(formData.username)) {
      setErrorMessage('Username must be alphanumeric and less than 30 characters');
      return;
    }

    // validate password strength
    if (!isPasswordStrong(formData.password)) {
      setErrorMessage('Password must be at least 8 characters long, with uppercase, lowercase, numbers, and special characters.');
      return;
    }

    setLoading(true);

    try {
      const response = await checkRegDetails(formData);
      if (response.status === 200) {
        const userData = {
          email: formData.email,
          username: formData.username,
          password: formData.password,
          agreeTerms,
          agreePrivacy,
          agreeMarketingComms,
          agreeReminders,
        };
        const createResp = await createUser(userData);
        if (createResp.status === 201) {
          setUserCreated(true);
        }
      }
    } catch (error) {
      if (error.response?.status === 400) {
        console.error(error);
        setErrorMessage('Username or email already in use. Please choose a different one.');
      } else if (error.response.status === 422) {
        console.error(error);
        setErrorMessage(error.response.data?.message || 'Invalid registration details.');
      } else {
        console.error(error);
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Register | LingoLeaf</title>
        <meta name="description" content="Create your LingoLeaf account to begin learning languages through engaging short stories with helpful translations and annotations." />
      </Helmet>
      <div className={pageStyles.styledPage}>
        <h1>Register</h1>
        <p>You don't need a user account to use LingoLeaf, but if you have one you can track and save your progress.</p>
        <p>Already have an account? Sign in <NavLink to="/login">here</NavLink>.</p>
        <p>If you've registered for an account but need a new confirmation email, get one <NavLink to="/resend-confirmation">here</NavLink>.</p>
        <form onSubmit={handleRegistration} className={formStyles.styledForm}>
          <label>
            Username:
            <input
              type="text"
              name="username"
              placeholder="Username"
              value={formData.username}
              onChange={handleInputChange}
              autoComplete="username"
              autoCorrect="off" 
              autoCapitalize="none"
              required
            />
          </label>
          <label>
            Email:
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              autoComplete="email"
              required
            />
          </label>
          <label>
            Password:
            <input
              type="password"
              name="password"
              placeholder="Password" 
              value={formData.password}
              onChange={handleInputChange}
              autoComplete="new-password"
              required
            />
          </label>
          <label>
            Confirm Password:
            <input
              type="password"
              name="confirmPassword"
              placeholder="Password" 
              value={formData.confirmPassword}
              onChange={handleInputChange}
              autoComplete="new-password"
              required
            />
          </label>
          <div className={formStyles.checkboxContainer}>
            <label>
              <input
                type="checkbox"
                name="agreeTerms"
                checked={agreeTerms}
                onChange={handleCheckboxChange}
              />
              I agree to the <NavLink to="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</NavLink>.
            </label>
            <label>
                <input
                  type="checkbox"
                  name="agreePrivacy"
                  checked={agreePrivacy}
                  onChange={handleCheckboxChange}
                />
                I agree to the <NavLink to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</NavLink> (including essential communications and "keep me logged in" cookies).
            </label>
            <label>
                <input
                  type="checkbox"
                  name="agreeMarketingComms"
                  checked={agreeMarketingComms}
                  onChange={handleCheckboxChange}
                />
                (OPTIONAL) I would like communications about news and special promotions. Opt out any time.
            </label>
            <label>
                <input
                  type="checkbox"
                  name="agreeReminders"
                  checked={agreeReminders}
                  onChange={handleCheckboxChange}
                />
                (OPTIONAL) I would like to receive reminders to use LingoLeaf. Opt out any time.
            </label>
          </div>
          <button type="submit" disabled={loading || !agreeTerms || !agreePrivacy}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>
        {errorMessage && <div className={pageStyles.alertBanner}><p>{errorMessage}</p></div>}
      </div>
    </>
  );
};

export default Register;